
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';
import { motion, cubicBezier } from 'framer-motion';
import { TopsIcon, InstagramIcon, PinterestIcon, TwitterIcon } from '../components/icons';
import { TikTokIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { AppPage } from '../types';
import { authors } from '@shared/data/authorsData';
import { CDN_URLS, APP_CONFIG, SOCIAL_LINKS } from '../config';
import { useSEO } from '../hooks/useSEO';
import Footer from '../components/Footer';

interface AboutPageProps {
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}


const AboutPage: React.FC<AboutPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();

  useSEO(
    t('seo_about_title'),
    t('seo_about_description'),
    [t('seo_about_kw1'), t('seo_about_kw2'), t('seo_about_kw3'), t('seo_about_kw4')]
  );

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1), staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };


  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
      className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-[#18181B] text-gray-800 dark:text-[#F1F1F1] overflow-y-auto custom-scrollbar"
    >
      <div className="max-w-4xl w-full bg-white dark:bg-[#18181B] shadow-lg rounded-xl p-6 md:p-10 mb-8 border border-gray-200 dark:border-[#C0A172]/10">
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-10">
          <TopsIcon className="w-12 h-12 text-gray-700 dark:text-[#C0A172] mb-4" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] text-center leading-tight">
            {t('aboutPage_title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-[#F1F1F1]/70 mt-3 text-center max-w-2xl">
            {t('aboutPage_tagline')}
          </p>
        </motion.div>

        <motion.section variants={itemVariants} className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-[#F1F1F1]/80">
          <p>{t('aboutPage_paragraph1')}</p>
          <p>{t('aboutPage_paragraph2')}</p>
        </motion.section>

        {/* Competitive Difference Section */}
        <motion.section variants={sectionVariants} className="mt-12 py-10 px-6 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-[#2A2A2A] dark:to-[#333] rounded-3xl border border-indigo-100 dark:border-[#C0A172]/20">
          <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-8 text-center uppercase tracking-[0.2em]">
            {t('aboutPage_differenceTitle', 'Not Just a Fitting Room')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/60 dark:bg-black/20 p-6 rounded-xl backdrop-blur-sm">
              <h3 className="font-bold text-lg mb-3 text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 pb-2">Create Standard VTO</h3>
              <ul className="space-y-3 text-gray-600 dark:text-gray-400 text-sm">
                <li className="flex items-start gap-2"><span className="text-red-400">×</span> Focuses only on size and technical fit</li>
                <li className="flex items-start gap-2"><span className="text-red-400">×</span> Static, clinical studio backgrounds</li>
                <li className="flex items-start gap-2"><span className="text-red-400">×</span> Highlights imperfections in user photos</li>
              </ul>
            </div>
            <div className="bg-white dark:bg-[#18181B] p-6 rounded-xl shadow-lg border border-indigo-100 dark:border-[#C0A172]/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#C0A172] text-white text-[10px] uppercase font-bold px-3 py-1 rounded-bl-lg">The Constantina Way</div>
              <h3 className="font-bold text-lg mb-3 text-[#C0A172]">Your Personal Styling Studio</h3>
              <ul className="space-y-3 text-gray-800 dark:text-gray-200 text-sm">
                <li className="flex items-start gap-2"><span className="text-green-500">✓</span> <strong>Emotional Confidence:</strong> We enhance your look to boost self-esteem.</li>
                <li className="flex items-start gap-2"><span className="text-green-500">✓</span> <strong>Lifestyle Context:</strong> See yourself in Paris, NYC, or a Gala.</li>
                <li className="flex items-start gap-2"><span className="text-green-500">✓</span> <strong>AI Stylist Persona:</strong> Expert advice based on your Color Season & Body Type.</li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* How It Works Layer */}
        <motion.section variants={sectionVariants} className="mt-16 text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#C0A172] mb-12 uppercase tracking-[0.3em]">
            {t('home_how_it_works_title', 'How it Works (The 3 Pillars)')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            {[1, 2, 3].map(idx => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="p-8 bg-white dark:bg-[#2A2A2A] rounded-2xl border border-gray-100 dark:border-[#C0A172]/10 shadow-sm hover:shadow-md transition-shadow group flex flex-col items-start"
              >
                <h3 className="text-xl font-bold text-gray-900 dark:text-[#F1F1F1] mb-4 group-hover:text-[#C0A172] transition-colors">{t(`home_how_it_works_step${idx}_title` as any)}</h3>
                <p className="text-gray-600 dark:text-[#F1F1F1]/60 leading-relaxed">{t(`home_how_it_works_step${idx}_desc` as any)}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section variants={sectionVariants} className="mt-20 text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#C0A172] mb-6 uppercase tracking-[0.3em]">{t('homePage_visionTitle')}</h2>
          <div className="mx-auto max-w-3xl space-y-6 text-xl text-gray-600 dark:text-[#F1F1F1]/90 italic font-serif leading-relaxed">
            <p className="before:content-['“'] after:content-['”']">
              {t('aboutPage_missionText')}
            </p>
          </div>
        </motion.section>

        {/* Certification Section */}
        <motion.section variants={sectionVariants} className="mt-20 py-16 px-6 bg-gray-50 dark:bg-[#2A2A2A] rounded-3xl text-center border border-gray-100 dark:border-[#C0A172]/10">
          <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-4 uppercase tracking-[0.2em]">{t('about_certification_title')}</h2>
          <p className="text-lg text-gray-600 dark:text-[#F1F1F1]/60 max-w-2xl mx-auto mb-12">{t('about_certification_desc')}</p>
          <div className="flex flex-wrap justify-center gap-16 opacity-60 dark:opacity-80 grayscale hover:grayscale-0 transition-all duration-500">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-bold tracking-tighter dark:text-[#F1F1F1]">Coloristika Bern</span>
              <span className="text-xs uppercase tracking-widest mt-2 text-[#C0A172] font-bold">{t('aboutPage_certified_milano')}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-3xl font-serif italic dark:text-[#F1F1F1]">Fashion College</span>
              <span className="text-xs uppercase tracking-widest mt-2 text-[#C0A172] font-bold">{t('aboutPage_certified_london')}</span>
            </div>
          </div>
        </motion.section>

        <motion.section variants={sectionVariants} className="mt-12 text-center py-12 bg-gray-50 dark:bg-[#2A2A2A] rounded-3xl border border-gray-100 dark:border-[#C0A172]/10">
          <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-8 uppercase tracking-[0.2em]">{t('aboutPage_techTitle')}</h2>
          <div className="max-w-3xl mx-auto space-y-6 text-gray-600 dark:text-[#F1F1F1]/70 leading-relaxed px-6">
            <p>{t('aboutPage_techText_p1')}</p>
            <p>{t('aboutPage_techText_p2')}</p>
          </div>
        </motion.section>

        <motion.section variants={sectionVariants} className="mt-20 text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-12 uppercase tracking-[0.2em]">{t('aboutPage_teamTitle')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {authors.map(author => (
              <motion.div key={author.id} variants={itemVariants} className="flex flex-col items-center">
                <div className="relative mb-6">
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#C0A172] to-[#F1F1F1] rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                  <img src={`${CDN_URLS.GITHUB}${author.avatarUrl}`} alt={t(author.nameKey)} className="relative w-28 h-28 rounded-full object-cover shadow-2xl" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 dark:text-[#F1F1F1]">{t(author.nameKey)}</h3>
                <p className="text-xs text-[#C0A172] font-black uppercase tracking-widest mt-1">{t(`${author.id}_role` as any)}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section variants={sectionVariants} className="mt-20 pt-12 border-t border-gray-100 dark:border-[#C0A172]/10 text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-4 uppercase tracking-[0.2em]">{t('aboutPage_socialTitle')}</h2>
          <p className="text-gray-600 dark:text-[#F1F1F1]/60 mb-8 max-w-lg mx-auto leading-relaxed">{t('aboutPage_socialText')}</p>
          <div className="flex justify-center gap-12">
            <motion.a href={SOCIAL_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C0A172] dark:hover:text-[#C0A172] transition-colors" variants={itemVariants}>
              <InstagramIcon className="w-8 h-8" />
            </motion.a>
            <motion.a href={SOCIAL_LINKS.PINTEREST} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C0A172] dark:hover:text-[#C0A172] transition-colors" variants={itemVariants}>
              <PinterestIcon className="w-8 h-8" />
            </motion.a>
            <motion.a href={SOCIAL_LINKS.TIKTOK} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C0A172] dark:hover:text-[#C0A172] transition-colors" variants={itemVariants}>
              <TikTokIcon className="w-8 h-8" />
            </motion.a>
            <motion.a href={SOCIAL_LINKS.TWITTER} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C0A172] dark:hover:text-[#C0A172] transition-colors" variants={itemVariants}>
              <TwitterIcon className="w-8 h-8" />
            </motion.a>
          </div>
        </motion.section>
        <motion.div variants={itemVariants} className="text-center mt-20">
          <button
            onClick={() => onNavigate('contact' as any)}
            className="bg-[#C0A172] hover:bg-[#A68A5E] text-white font-bold py-4 px-12 rounded-full transition-all duration-300 transform active:scale-95 text-lg shadow-xl"
          >
            {t('aboutPage_contactUs')}
          </button>
        </motion.div>
      </div>
      <Footer onNavigate={onNavigate} />
    </motion.div>
  );
};

export default AboutPage;
